'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { M } from '../lesson/Math'
import { Controls, fmt, Readout, Segmented, Slider, usePrefersReducedMotion } from './controls'
import { Arrow, Plot, Polyline, Pt, Seg, Txt } from './Plot'

type V = [number, number]
const add = (a: V, b: V): V => [a[0] + b[0], a[1] + b[1]]
const scale = (a: V, k: number): V => [a[0] * k, a[1] * k]
const len = (a: V) => Math.hypot(a[0], a[1])
const dot = (a: V, b: V) => a[0] * b[0] + a[1] * b[1]
const r1 = (x: number) => Math.round(x * 10) / 10

// ─── Vectors ───────────────────────────────────────────────────────────────────

/** Drag vector tips; modes show components, addition (tip-to-tail) or scaling. */
export function VectorPlayground({ initialMode = 'components' }: { initialMode?: 'components' | 'add' | 'scale' }) {
  const [a, setA] = useState<V>([3, 2])
  const [b, setB] = useState<V>([-1, 2.5])
  const [k, setK] = useState(1.5)
  const [mode, setMode] = useState(initialMode)
  const sum = add(a, b)
  const ka = scale(a, k)
  return (
    <div>
      <Plot x={[-6, 6]} y={[-4.5, 5.5]} height={400} ariaLabel="Vectors on a plane">
        {mode === 'components' && (
          <>
            <Seg x1={0} y1={0} x2={a[0]} y2={0} color="blue" width={2} dashed />
            <Seg x1={a[0]} y1={0} x2={a[0]} y2={a[1]} color="amber" width={2} dashed />
            <Txt x={a[0] / 2} y={0} dy={a[1] >= 0 ? 16 : -8} anchor="middle" color="blue" size={12}>
              {`x = ${fmt(a[0], 1)}`}
            </Txt>
            <Txt x={a[0]} y={a[1] / 2} dx={8} color="amber" size={12}>
              {`y = ${fmt(a[1], 1)}`}
            </Txt>
            <Txt x={a[0] / 2} y={a[1] / 2} dx={-14} dy={-8} anchor="end" color="ink" size={12}>
              {`length ${fmt(len(a), 2)}`}
            </Txt>
          </>
        )}
        {mode === 'add' && (
          <>
            <Arrow x1={a[0]} y1={a[1]} x2={sum[0]} y2={sum[1]} color="green" width={2} dashed opacity={0.6} />
            <Arrow x1={b[0]} y1={b[1]} x2={sum[0]} y2={sum[1]} color="blue" width={2} dashed opacity={0.6} />
            <Arrow x1={0} y1={0} x2={sum[0]} y2={sum[1]} color="accent" width={3.2} label="a + b" />
          </>
        )}
        {mode === 'scale' && <Arrow x1={0} y1={0} x2={ka[0]} y2={ka[1]} color="accent" width={3.2} label={`${fmt(k, 2)} · a`} labelOffset={[10, 14]} opacity={0.9} />}
        <Arrow x1={0} y1={0} x2={a[0]} y2={a[1]} color="blue" width={3} label="a" />
        {mode === 'add' && <Arrow x1={0} y1={0} x2={b[0]} y2={b[1]} color="green" width={3} label="b" />}
        <Pt x={a[0]} y={a[1]} onDrag={(x, y) => setA([r1(x), r1(y)])} color="blue" r={6} ariaLabel="Tip of vector a" />
        {mode === 'add' && <Pt x={b[0]} y={b[1]} onDrag={(x, y) => setB([r1(x), r1(y)])} color="green" r={6} ariaLabel="Tip of vector b" />}
      </Plot>
      <Controls>
        <Segmented label="Mode" value={mode} onChange={setMode} options={[{ value: 'components', label: 'components' }, { value: 'add', label: 'add' }, { value: 'scale', label: 'scale' }]} />
        {mode === 'scale' && (
          <div className="min-w-[220px] flex-1">
            <Slider label="k" value={k} onChange={setK} min={-2} max={2.5} step={0.05} role="accent" />
          </div>
        )}
        <span className="text-[16px]">
          {mode === 'components' && <M>{`\\blue{\\mathbf{a}} = [${fmt(a[0], 1)},\\ ${fmt(a[1], 1)}]`}</M>}
          {mode === 'add' && <M>{`[${fmt(a[0], 1)}, ${fmt(a[1], 1)}] + [${fmt(b[0], 1)}, ${fmt(b[1], 1)}] = \\accent{[${fmt(sum[0], 1)}, ${fmt(sum[1], 1)}]}`}</M>}
          {mode === 'scale' && <M>{`${fmt(k, 2)} \\times [${fmt(a[0], 1)}, ${fmt(a[1], 1)}] = \\accent{[${fmt(ka[0], 2)}, ${fmt(ka[1], 2)}]}`}</M>}
        </span>
      </Controls>
    </div>
  )
}

// ─── Dot product ───────────────────────────────────────────────────────────────

export function DotProductViz({ onPerpendicular }: { onPerpendicular?: () => void }) {
  const [a, setA] = useState<V>([3, 1])
  const [b, setB] = useState<V>([1.5, 2.5])
  const fired = useRef(false)
  const d = dot(a, b)
  const la = len(a)
  const lb = len(b)
  const cos = la && lb ? d / (la * lb) : 0
  const angle = (Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI
  // projection of b onto a
  const pk = la ? d / (la * la) : 0
  const proj = scale(a, pk)
  const zero = Math.abs(d) < 0.15 && la > 0.5 && lb > 0.5
  useEffect(() => {
    if (zero && !fired.current) {
      fired.current = true
      onPerpendicular?.()
    }
  }, [zero, onPerpendicular])
  const color = zero ? 'var(--gd-ink-3)' : d > 0 ? 'var(--gd-green)' : 'var(--gd-pink)'
  return (
    <div>
      <Plot x={[-5, 5]} y={[-4, 4.5]} height={360} ariaLabel="Two vectors and their dot product">
        <Seg x1={b[0]} y1={b[1]} x2={proj[0]} y2={proj[1]} color="var(--gd-ink-3)" dashed width={1.2} />
        <Seg x1={0} y1={0} x2={proj[0]} y2={proj[1]} color={color} width={6} opacity={0.35} />
        <Arrow x1={0} y1={0} x2={a[0]} y2={a[1]} color="blue" width={3} label="a" />
        <Arrow x1={0} y1={0} x2={b[0]} y2={b[1]} color="amber" width={3} label="b" />
        <Pt x={a[0]} y={a[1]} onDrag={(x, y) => setA([r1(x), r1(y)])} color="blue" r={6} ariaLabel="Tip of vector a" />
        <Pt x={b[0]} y={b[1]} onDrag={(x, y) => setB([r1(x), r1(y)])} color="amber" r={6} ariaLabel="Tip of vector b" />
        <Txt x={-4.8} y={4} size={13} weight={650} color={color}>
          {zero ? 'perpendicular: a · b = 0' : d > 0 ? 'pointing the same way: a · b > 0' : 'pointing apart: a · b < 0'}
        </Txt>
      </Plot>
      <Controls>
        <span className="text-[16px]">
          <M>{`\\blue{a}\\cdot\\amber{b} = (${fmt(a[0], 1)})(${fmt(b[0], 1)}) + (${fmt(a[1], 1)})(${fmt(b[1], 1)}) =`}</M>
        </span>
        <Readout label="dot product" value={fmt(d, 2)} role={zero ? 'ink' : d > 0 ? 'green' : 'pink'} big />
        <Readout label="angle" value={`${fmt(angle, 0)}°`} />
        <Readout label="cosine similarity" value={fmt(cos, 3)} role="violet" />
      </Controls>
    </div>
  )
}

// ─── Matrices as transformations ──────────────────────────────────────────────

const PRESETS: Record<string, { label: string; m: [number, number, number, number] }> = {
  identity: { label: 'identity', m: [1, 0, 0, 1] },
  stretch: { label: 'stretch', m: [2, 0, 0, 1] },
  rotate: { label: 'rotate 90°', m: [0, -1, 1, 0] },
  shear: { label: 'shear', m: [1, 1, 0, 1] },
  flip: { label: 'mirror', m: [-1, 0, 0, 1] },
  squash: { label: 'squash flat', m: [1, 0.5, 2, 1] },
}

const SHAPE: V[] = [
  [0, 0],
  [0, 2],
  [1.3, 2],
  [1.3, 1.6],
  [0.4, 1.6],
  [0.4, 1.15],
  [1.1, 1.15],
  [1.1, 0.8],
  [0.4, 0.8],
  [0.4, 0],
]

export function MatrixTransform({ goal, onGoal }: { goal?: [number, number, number, number]; onGoal?: () => void }) {
  const [m, setM] = useState<[number, number, number, number]>([1, 0, 0, 1])
  const [t, setT] = useState(1)
  const reduced = usePrefersReducedMotion()
  const fired = useRef(false)
  // Animate from identity to the matrix with t (0 → 1)
  const [a, b, c, d] = m.map((v, i) => (i === 0 || i === 3 ? 1 + (v - 1) * t : v * t)) as [number, number, number, number]
  const apply = (p: V): V => [a * p[0] + b * p[1], c * p[0] + d * p[1]]
  const det = m[0] * m[3] - m[1] * m[2]
  const hit = goal ? m.every((v, i) => Math.abs(v - goal[i]) < 0.051) : false

  useEffect(() => {
    if (hit && !fired.current) {
      fired.current = true
      onGoal?.()
    }
  }, [hit, onGoal])

  function animateTo(next: [number, number, number, number]) {
    setM(next)
    if (reduced) return setT(1)
    setT(0)
    let start: number | null = null
    const tick = (now: number) => {
      if (start === null) start = now
      const p = Math.min(1, (now - start) / 700)
      setT(1 - Math.pow(1 - p, 3))
      if (p < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }

  const gridLines = useMemo(() => Array.from({ length: 13 }, (_, i) => i - 6), [])
  const i = apply([1, 0])
  const j = apply([0, 1])

  return (
    <div>
      <div className="grid gap-5 md:grid-cols-[1fr_220px]">
        <Plot x={[-5, 5]} y={[-4, 4]} height={380} grid={false} ariaLabel="A matrix transforming the plane">
          {gridLines.map(g => (
            <g key={g}>
              <Polyline points={[apply([g, -6]), apply([g, 6])]} color={g === 0 ? 'var(--gd-ink-3)' : 'var(--gd-rule-2)'} width={g === 0 ? 1.4 : 1} />
              <Polyline points={[apply([-6, g]), apply([6, g])]} color={g === 0 ? 'var(--gd-ink-3)' : 'var(--gd-rule-2)'} width={g === 0 ? 1.4 : 1} />
            </g>
          ))}
          <Polyline points={[[0, 0], apply([1, 0]), apply([1, 1]), apply([0, 1])]} closed fill="color-mix(in srgb, var(--gd-violet) 16%, transparent)" color="violet" width={1} opacity={0.9} />
          <Polyline points={SHAPE.map(apply)} closed fill="color-mix(in srgb, var(--gd-amber) 30%, transparent)" color="amber" width={2} />
          <Arrow x1={0} y1={0} x2={i[0]} y2={i[1]} color="green" width={3} label="î" />
          <Arrow x1={0} y1={0} x2={j[0]} y2={j[1]} color="pink" width={3} label="ĵ" />
        </Plot>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="gd-serif text-[64px] leading-none" style={{ color: 'var(--gd-ink-3)' }}>
              [
            </span>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              {m.map((v, k) => (
                <input
                  key={k}
                  type="number"
                  step={0.1}
                  value={v}
                  onChange={e => setM(prev => prev.map((pv, pk) => (pk === k ? Number(e.target.value) || 0 : pv)) as [number, number, number, number])}
                  className="gd-input !h-10 w-[68px] !px-2 text-center"
                  style={{ color: k % 2 === 0 ? 'var(--gd-green)' : 'var(--gd-pink)' }}
                  aria-label={`Matrix entry row ${Math.floor(k / 2) + 1}, column ${(k % 2) + 1}`}
                />
              ))}
            </div>
            <span className="gd-serif text-[64px] leading-none" style={{ color: 'var(--gd-ink-3)' }}>
              ]
            </span>
          </div>
          <p className="text-[13.5px] leading-snug" style={{ color: 'var(--gd-ink-2)' }}>
            <span style={{ color: 'var(--gd-green)' }}>Column 1</span> is where î lands; <span style={{ color: 'var(--gd-pink)' }}>column 2</span> is where ĵ lands.
          </p>
          <Readout label="area scale (determinant)" value={fmt(det, 2)} role="violet" />
          {goal && hit && <span className="gd-chip self-start" style={{ color: 'var(--gd-green)', borderColor: 'var(--gd-green)' }}>✓ matrix matches</span>}
        </div>
      </div>
      <Controls>
        {Object.entries(PRESETS).map(([key, p]) => (
          <button key={key} type="button" className="gd-btn gd-btn-ghost gd-btn-xs" onClick={() => animateTo(p.m)}>
            {p.label}
          </button>
        ))}
        <button type="button" className="gd-btn gd-btn-ink gd-btn-xs" onClick={() => animateTo([...m])}>
          Replay
        </button>
      </Controls>
    </div>
  )
}

// ─── Matrix multiplication, one cell at a time ───────────────────────────────

const A = [
  [1, 2, 0],
  [3, -1, 2],
]
const B = [
  [2, 1],
  [0, 3],
  [1, -2],
]

function cellStyle(on: boolean, color: string) {
  return {
    background: on ? `color-mix(in srgb, ${color} 18%, transparent)` : 'transparent',
    borderColor: on ? color : 'transparent',
  }
}

function Mat({ data, highlight, color, filled }: { data: number[][]; highlight: (i: number, j: number) => boolean; color: string; filled?: (i: number, j: number) => boolean }) {
  return (
    <div className="inline-grid gap-1 rounded-[10px] border-x-2 px-2 py-1" style={{ gridTemplateColumns: `repeat(${data[0].length}, 44px)`, borderColor: 'var(--gd-ink-3)' }}>
      {data.map((row, i) =>
        row.map((v, j) => (
          <span key={`${i}-${j}`} className="gd-num flex h-10 items-center justify-center rounded-[6px] border-2 text-[16px] transition-all duration-300" style={{ ...cellStyle(highlight(i, j), color), opacity: filled && !filled(i, j) ? 0.15 : 1 }}>
            {v}
          </span>
        )),
      )}
    </div>
  )
}

export function MatMulStepper() {
  const [step, setStep] = useState(0)
  const [playing, setPlaying] = useState(false)
  const reduced = usePrefersReducedMotion()
  const cells = A.length * B[0].length
  const ci = step > 0 ? Math.floor((step - 1) / B[0].length) : -1
  const cj = step > 0 ? (step - 1) % B[0].length : -1
  const C = A.map(row => B[0].map((_, j) => row.reduce((s, v, k) => s + v * B[k][j], 0)))

  useEffect(() => {
    if (!playing) return
    if (step >= cells) {
      const t = setTimeout(() => setPlaying(false), 0)
      return () => clearTimeout(t)
    }
    const t = setTimeout(() => setStep(s => s + 1), reduced ? 200 : 1100)
    return () => clearTimeout(t)
  }, [playing, step, cells, reduced])

  const done = (i: number, j: number) => i * B[0].length + j < step

  return (
    <div>
      <div className="flex flex-wrap items-center justify-center gap-4 py-3 text-[20px]">
        <div className="text-center">
          <Mat data={A} highlight={i => i === ci} color="var(--gd-blue)" />
          <div className="gd-label mt-1 !text-[10px]">A · 2×3</div>
        </div>
        <span style={{ color: 'var(--gd-ink-3)' }}>×</span>
        <div className="text-center">
          <Mat data={B} highlight={(_, j) => j === cj} color="var(--gd-green)" />
          <div className="gd-label mt-1 !text-[10px]">B · 3×2</div>
        </div>
        <span style={{ color: 'var(--gd-ink-3)' }}>=</span>
        <div className="text-center">
          <Mat data={C} highlight={(i, j) => i === ci && j === cj} color="var(--gd-amber)" filled={done} />
          <div className="gd-label mt-1 !text-[10px]">C · 2×2</div>
        </div>
      </div>
      <div className="min-h-[44px] text-center text-[17px]">
        {ci >= 0 ? (
          <M>{`C_{${ci + 1}${cj + 1}} = ${A[ci].map((v, k) => `\\blue{${v}}\\cdot\\green{${B[k][cj]}}`).join(' + ')} = \\amber{${C[ci][cj]}}`}</M>
        ) : (
          <span style={{ color: 'var(--gd-ink-3)' }}>Each cell of C is one row of A dotted with one column of B.</span>
        )}
      </div>
      <Controls className="justify-center">
        <button type="button" className="gd-btn gd-btn-ghost gd-btn-xs" onClick={() => setStep(s => Math.min(cells, s + 1))} disabled={step >= cells}>
          Next cell
        </button>
        <button type="button" className="gd-btn gd-btn-ink gd-btn-xs" onClick={() => { if (step >= cells) setStep(0); setPlaying(p => !p) }}>
          {playing ? 'Pause' : step >= cells ? 'Replay' : 'Play'}
        </button>
        <button type="button" className="gd-btn gd-btn-ghost gd-btn-xs" onClick={() => { setStep(0); setPlaying(false) }}>
          Reset
        </button>
      </Controls>
    </div>
  )
}

// ─── A layer is a matrix ──────────────────────────────────────────────────────

/** Inputs → weighted connections → outputs, where each output is a row·x dot product. */
export function LayerAsMatrix() {
  const [x, setX] = useState([1, 0.5, -1])
  const W = [
    [0.8, -0.5, 0.2],
    [-0.3, 0.9, 0.6],
  ]
  const y = W.map(row => row.reduce((s, w, k) => s + w * x[k], 0))
  const H = 220
  const inY = (k: number) => 40 + k * 70
  const outY = (k: number) => 75 + k * 70
  return (
    <div className="grid gap-6 md:grid-cols-[1fr_1fr] md:items-center">
      <svg viewBox={`0 0 360 ${H}`} className="w-full" role="img" aria-label="A layer of two neurons reading three inputs">
        {W.map((row, o) =>
          row.map((w, k) => (
            <line key={`${o}-${k}`} x1={70} y1={inY(k)} x2={290} y2={outY(o)} stroke={w >= 0 ? 'var(--gd-green)' : 'var(--gd-pink)'} strokeWidth={Math.abs(w) * 5 + 0.5} opacity={0.65} />
          )),
        )}
        {x.map((v, k) => (
          <g key={k}>
            <circle cx={70} cy={inY(k)} r={22} fill="var(--gd-surface)" stroke="var(--gd-blue)" strokeWidth={2.5} />
            <text x={70} y={inY(k) + 5} textAnchor="middle" fontSize={14} fill="var(--gd-blue)" fontWeight={600}>
              {fmt(v, 1)}
            </text>
          </g>
        ))}
        {y.map((v, o) => (
          <g key={o}>
            <circle cx={290} cy={outY(o)} r={24} fill="var(--gd-surface)" stroke="var(--gd-amber)" strokeWidth={2.5} />
            <text x={290} y={outY(o) + 5} textAnchor="middle" fontSize={14} fill="var(--gd-amber)" fontWeight={600}>
              {fmt(v, 2)}
            </text>
          </g>
        ))}
      </svg>
      <div>
        <div className="text-[17px]">
          <M>{`\\begin{bmatrix} \\green{0.8} & \\pink{-0.5} & \\green{0.2} \\\\ \\pink{-0.3} & \\green{0.9} & \\green{0.6} \\end{bmatrix} \\begin{bmatrix} \\blue{${fmt(x[0], 1)}} \\\\ \\blue{${fmt(x[1], 1)}} \\\\ \\blue{${fmt(x[2], 1)}} \\end{bmatrix} = \\begin{bmatrix} \\amber{${fmt(y[0], 2)}} \\\\ \\amber{${fmt(y[1], 2)}} \\end{bmatrix}`.replace(/−/g, '-')}</M>
        </div>
        <div className="mt-4 grid gap-2">
          {x.map((v, k) => (
            <Slider key={k} label={<M>{`\\blue{x_${k + 1}}`}</M>} value={v} onChange={nv => setX(prev => prev.map((pv, pk) => (pk === k ? nv : pv)))} min={-2} max={2} step={0.1} role="blue" digits={1} />
          ))}
        </div>
      </div>
    </div>
  )
}

export function DotStrip({ a, b }: { a: number[]; b: number[] }) {
  return (
    <div className="overflow-x-auto py-2">
      <div className="inline-grid gap-x-2 gap-y-1 text-center" style={{ gridTemplateColumns: `auto repeat(${a.length}, 52px) auto 64px` }}>
        <span className="gd-label self-center pr-2 !text-[10px]">a</span>
        {a.map((v, i) => (
          <span key={i} className="gd-num rounded-[6px] border py-1.5" style={{ borderColor: 'var(--gd-blue)', color: 'var(--gd-blue)' }}>
            {v}
          </span>
        ))}
        <span />
        <span />
        <span className="gd-label self-center pr-2 !text-[10px]">b</span>
        {b.map((v, i) => (
          <span key={i} className="gd-num rounded-[6px] border py-1.5" style={{ borderColor: 'var(--gd-amber)', color: 'var(--gd-amber)' }}>
            {v}
          </span>
        ))}
        <span />
        <span />
        <span className="gd-label self-center pr-2 !text-[10px]">a×b</span>
        {a.map((v, i) => (
          <span key={i} className="gd-num py-1.5" style={{ color: 'var(--gd-ink)' }}>
            {fmt(v * b[i], 2)}
          </span>
        ))}
        <span className="self-center" style={{ color: 'var(--gd-ink-3)' }}>
          Σ →
        </span>
        <span className="gd-num self-center rounded-[6px] py-1.5 font-semibold" style={{ background: 'color-mix(in srgb, var(--gd-green) 15%, transparent)', color: 'var(--gd-green)' }}>
          {fmt(a.reduce((s, v, i) => s + v * b[i], 0), 2)}
        </span>
      </div>
    </div>
  )
}

